import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldPlus, CheckCircle2, Search, Download, Wallet, BadgeDollarSign, Banknote, ReceiptText } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { formatCurrency } from '../mock';
import { useAuth } from '../context/AuthContext';
import { Commissions } from './finance/Commissions';
import { Fundings } from './finance/Fundings';
import { Payoffs } from './finance/Payoffs';
import { Payables } from './finance/Payables';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const Financeiro = () => {
  const { user } = useAuth();
  const canFin = ['Administrator', 'Owner'].includes(user?.role) || (user?.role || '').endsWith('Manager') || (user?.permissions || []).includes('view_finance');
  const SUBMENUS = [...(canFin ? [['warranties', ShieldPlus, 'Garantia Estendida'], ['fundings', BadgeDollarSign, 'Fundings'], ['payoffs', Banknote, 'Payoffs'], ['payables', ReceiptText, 'Contas a Pagar']] : []), ['commissions', Wallet, 'Comissões']];
  const [sub, setSub] = useState(canFin ? 'warranties' : 'commissions');
  const [data, setData] = useState({ rows: [], summary: {} });
  const [filter, setFilter] = useState('unpaid');
  const [month, setMonth] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    if (sub !== 'warranties') return;
    try {
      const { data: d } = await api.get('/finance/warranties', { params: { status: filter, month } });
      setData(d);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
  }, [filter, month, sub]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return data.rows;
    return data.rows.filter((r) => [r.customer_name, r.vehicle_desc, r.stock, r.company, r.deal_id].join(' ').toLowerCase().includes(s));
  }, [data.rows, q]);

  const togglePaid = async (r) => {
    if (!r.paid && !window.confirm(`Confirmar o pagamento da garantia de ${r.customer_name} (${formatCurrency(r.cost)})?`)) return;
    setBusy(r.deal_id);
    try {
      await api.put(`/finance/warranties/${r.deal_id}/payment`, { paid: !r.paid });
      toast({ title: r.paid ? 'Pagamento reaberto' : 'Pagamento confirmado' });
      await load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(''); }
  };

  const exportCsv = () => {
    const head = ['Cliente', 'Veiculo', 'Stock', 'Produto', 'Empresa', 'Prazo (meses)', 'Custo', 'Preco', 'Markup', 'Emitida em', 'Vencimento', 'Pago', 'Pago em', 'Pago por'];
    const lines = rows.map((r) => [r.customer_name, r.vehicle_desc, r.stock, r.product, r.company, r.term_months, r.cost, r.price, r.markup, (r.issued_at || '').slice(0, 10), r.due_date, r.paid ? 'Sim' : 'Nao', (r.paid_at || '').slice(0, 10), r.paid_by]);
    const csv = [head, ...lines].map((l) => l.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `garantias_emitidas_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (denied && sub === 'warranties') return <div className="ent-panel p-6 text-sm text-muted-foreground" data-testid="finance-denied">Somente Admin, Owner ou Managers acessam o módulo Financeiro.</div>;

  const s = data.summary || {};
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-3" data-testid="financeiro-page">
      <div className="ent-tabs" data-testid="financeiro-submenu">
        {SUBMENUS.map(([k, Icon, label]) => (
          <button key={k} data-testid={`finance-sub-${k}`} onClick={() => setSub(k)} className={`ent-tab ${sub === k ? 'ent-tab-active' : ''}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {sub === 'commissions' && <Commissions />}

      {sub === 'fundings' && <Fundings />}

      {sub === 'payoffs' && <Payoffs />}

      {sub === 'payables' && <Payables />}

      {sub === 'warranties' && <>
      <div className="ent-ribbon" data-testid="finance-ribbon">
        <div><p className="ent-label">Garantias emitidas</p><p className="ent-value mt-1" data-testid="wr-total">{s.total ?? 0}</p></div>
        <div><p className="ent-label">A pagar</p><p className="ent-value mt-1 t-warn" data-testid="wr-unpaid">{s.unpaid ?? 0}</p></div>
        <div><p className="ent-label">Valor a pagar</p><p className="ent-value mt-1 t-warn" data-testid="wr-amount-unpaid">{formatCurrency(s.amount_unpaid || 0)}</p></div>
        <div><p className="ent-label">Vencidas</p><p className={`ent-value mt-1 ${s.overdue ? 't-bad' : ''}`} data-testid="wr-overdue">{s.overdue ?? 0}</p></div>
        <div><p className="ent-label">Pagas</p><p className="ent-value mt-1 t-ok" data-testid="wr-paid">{s.paid ?? 0}</p></div>
        <div><p className="ent-label">Markup total</p><p className="ent-value mt-1" data-testid="wr-markup">{formatCurrency(s.markup_total || 0)}</p></div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[['unpaid', 'A pagar'], ['paid', 'Pagas'], ['', 'Todas']].map(([k, label]) => (
          <button key={k} data-testid={`wr-filter-${k || 'all'}`} onClick={() => setFilter(k)} className={`ent-btn ${filter === k ? 'ent-btn-primary' : ''}`}>{label}</button>
        ))}
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 pointer-events-none" />
          <input data-testid="wr-search" className="ent-input pl-8" style={{ width: 230 }} placeholder="Cliente, veículo, empresa..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <input data-testid="wr-month" type="month" className="ent-input" style={{ width: 'auto' }} value={month} onChange={(e) => setMonth(e.target.value)} />
        {month && <button data-testid="wr-month-clear" onClick={() => setMonth('')} className="ent-btn">Limpar mês</button>}
        <button data-testid="wr-export" onClick={exportCsv} className="ent-btn ml-auto"><Download className="w-3.5 h-3.5" /> Exportar CSV</button>
      </div>

      <div className="ent-panel overflow-hidden">
        <div className="ent-colhead" style={{ borderTop: '2px solid #a855f7' }}>
          <ShieldPlus className="w-3.5 h-3.5 text-fuchsia-400" />
          <span className="ent-coltitle uppercase tracking-wide">Garantias emitidas</span>
          <span className="ent-pill" data-testid="wr-count">{rows.length}</span>
          <span className="text-[11px] t-label ml-auto hidden sm:block">Pagamento devido até o último dia do mês da emissão</span>
        </div>
        <div className="overflow-x-auto">
          {rows.length === 0 ? (
            <p className="ent-empty">Nenhuma garantia emitida neste filtro</p>
          ) : (
            <table className="ent-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th className="hidden lg:table-cell">Veículo / Stock</th>
                  <th>Produto / Empresa</th>
                  <th className="hidden xl:table-cell">Prazo</th>
                  <th className="text-right">Custo</th>
                  <th className="text-right hidden xl:table-cell">Preço</th>
                  <th className="text-right hidden xl:table-cell">Markup</th>
                  <th className="hidden lg:table-cell">Emitida</th>
                  <th>Vencimento</th>
                  <th>Pagamento</th>
                  <th className="text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.deal_id} data-testid={`wr-row-${r.deal_id}`}>
                    <td>
                      <p className="font-medium">{r.customer_name}</p>
                      <p className="mono text-[11px] t-label">{r.deal_id}{r.salesperson ? ` · ${r.salesperson}` : ''}</p>
                    </td>
                    <td className="hidden lg:table-cell">
                      <p className="text-foreground/75">{r.vehicle_desc}</p>
                      <p className="mono text-[11px] t-label">#{r.stock}</p>
                    </td>
                    <td>
                      <p className="text-foreground/75">{r.product}</p>
                      <p className="text-[11px] t-label">{r.company || '—'}</p>
                    </td>
                    <td className="num hidden xl:table-cell">{r.term_months ? `${r.term_months} m` : '—'}</td>
                    <td className="num text-right font-semibold" data-testid={`wr-cost-${r.deal_id}`}>{formatCurrency(r.cost)}</td>
                    <td className="num text-right hidden xl:table-cell">{formatCurrency(r.price)}</td>
                    <td className="num text-right hidden xl:table-cell t-ok">{formatCurrency(r.markup)}</td>
                    <td className="num hidden lg:table-cell">{fmtDate(r.issued_at)}</td>
                    <td className={`num ${!r.paid && r.due_date && r.due_date < today ? 't-bad font-semibold' : ''}`}>{r.due_date ? r.due_date.split('-').reverse().join('/') : '—'}</td>
                    <td>
                      {r.paid ? (
                        <span className="ent-status t-ok" data-testid={`wr-status-${r.deal_id}`}><span className="ent-dot" />Paga {r.paid_at ? `· ${fmtDate(r.paid_at)}` : ''}</span>
                      ) : (
                        <span className="ent-status t-warn" data-testid={`wr-status-${r.deal_id}`}><span className="ent-dot" />Pendente</span>
                      )}
                    </td>
                    <td className="text-right">
                      <button data-testid={`wr-toggle-${r.deal_id}`} disabled={busy === r.deal_id} onClick={() => togglePaid(r)} className={`ent-btn ent-btn-sm ${r.paid ? '' : 'text-[hsl(var(--ok))] border-[hsl(var(--ok))]/50'}`}>
                        <CheckCircle2 className="w-3 h-3" /> {r.paid ? 'Reabrir' : 'Marcar paga'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      </>}
    </div>
  );
};

export default Financeiro;
